'use client';

import { c as _c } from "react/compiler-runtime";
import { TooltipButton } from '@/comps/tooltip-button';
import type { TimeRangeKind } from '@/api/generated/model';
import type { ScorePercentChange } from '@/domains/pages/ai-proficiency/utils/map-aggregates';
import { getAifiTrendVsLabel } from '@/domains/pages/ai-proficiency/constants';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { TrendingDown, TrendingUp } from 'lucide-react';
type ProficiencyTrendChipProps = {
  delta: Exclude<ScorePercentChange, null>;
  timeRangeKind: TimeRangeKind;
};
export function ProficiencyTrendChip(t0) {
  const $ = _c(17);
  const {
    delta,
    timeRangeKind
  } = t0;
  let t1;
  if ($[0] !== timeRangeKind) {
    t1 = getAifiTrendVsLabel(timeRangeKind);
    $[0] = timeRangeKind;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const vsLabel = t1;
  if (delta === "na") {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = cn("inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-base border px-1.5 py-1 text-[14px] leading-none text-muted-foreground", AIFI_DATA_NUMBER_CLASS);
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== vsLabel) {
      t3 = <TooltipButton content="Rate of change over the previous timespan. Adjust the timespan using the button in the upper right corner of the platform."><div className={t2}><span>N/A {vsLabel}</span></div></TooltipButton>;
      $[3] = vsLabel;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  const isPositive = delta >= 0;
  let t2;
  if ($[5] !== delta) {
    t2 = formatScore(delta, {
      decimals: 2
    });
    $[5] = delta;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const formattedDelta = `${isPositive ? "+" : ""}${t2}%`;
  const Icon = isPositive ? TrendingUp : TrendingDown;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-base border px-1.5 py-1 text-[14px] leading-none text-muted-foreground", AIFI_DATA_NUMBER_CLASS);
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = isPositive ? "text-positivev2" : "text-negativev2";
  let t5;
  if ($[8] !== Icon || $[9] !== t4) {
    t5 = <Icon size={12} className={t4} />;
    $[8] = Icon;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== formattedDelta || $[12] !== vsLabel) {
    t6 = <span>{formattedDelta} {vsLabel}</span>;
    $[11] = formattedDelta;
    $[12] = vsLabel;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <TooltipButton content="Rate of change over the previous timespan. Adjust the timespan using the button in the upper right corner of the platform."><div className={t3}>{t5}{t6}</div></TooltipButton>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}